import { CalloutComponent, FormValues, getValue, InputFormCallout, InputFormGroup } from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { LicenseTypeSelect } from "../../components/LicenseTypeSelect/LicenseTypeSelect";
import { AdbsDatabaseBaseLicenseModelEnum } from "../../gen/clients/mchub-azure-api-client";
import { parseId } from "../../helpers/idHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  ADBS_UPDATE_POLL_DELAY,
  ADBS_UPDATE_POLL_INTERVAL,
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export interface AdbsEditLicenseProps extends OperationActionProps {
  databaseId: string | undefined;
  licenseModel: AdbsDatabaseBaseLicenseModelEnum | undefined;
}

const enum Fields {
  LicenseType = "licenseType",
}

const AdbsEditLicense = (
  { targetId, location, databaseId, licenseModel, onExecute, onCancel }: AdbsEditLicenseProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
  const resGroup = decodeURIComponent(resourceGroup || "");
  const resName = decodeURIComponent(resourceName || "");
  const [licenseCalloutRef, setLicenseCalloutRef] = React.useState<CalloutComponent>({} as CalloutComponent);

  const { invokeCall: invokeAdbsUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).adbsDatabaseApi.updateAdbsDatabase },
  );

  const onLicenseSubmit = (formValues: FormValues): void => {
    const licenseModelValue = getValue(formValues, Fields.LicenseType, InputFormGroup)?.[0].id;

    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: () => {
        licenseCalloutRef.allowResubmit();
      },
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.updateAdbsLicense(),
          message: Messages.notifications.inProgress.messages.updateAdbsLicense(resName),
        },
        success: {
          title: Messages.notifications.success.titles.updateAdbsLicense(),
          message: Messages.notifications.success.messages.updateAdbsLicense(resName),
        },
        failure: {
          title: Messages.notifications.failure.titles.updateAdbsLicense(),
          message: Messages.notifications.failure.messages.updateAdbsLicense(resName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.ADBS_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName: resGroup,
            databaseName: resName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.ADBS_UPDATED_CHECK,
          delay: ADBS_UPDATE_POLL_DELAY,
          interval: ADBS_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokeAdbsUpdate({
      databaseName: resName,
      subscriptionId,
      resourceGroupName: resGroup,
      updateAdbsDatabaseDetails: { licenseModel: licenseModelValue },
    }, invokeOptions);
  };

  return (
    <InputFormCallout
      componentRef={setLicenseCalloutRef}
      targetId={targetId}
      title={Messages.createNewPanels.licenseType.title()}
      onSubmit={onLicenseSubmit}
      onClose={onCancel}
    >
      <LicenseTypeSelect
        fieldName={Fields.LicenseType}
        defaultValue={licenseModel}
        label={Messages.labels.licenseType()}
      />
    </InputFormCallout>
  );
};

export const newAdbsEditLicense = (
  props: AdbsEditLicenseProps,
): JSX.Element => (<AdbsEditLicense {...props} />);
